import { useCallback, useEffect, useReducer, useRef, useState } from 'react';

/**
 * useReducer con persistenza su localStorage.
 *
 * Serve all'asta offline, che prima non salvava assolutamente nulla: un refresh
 * cancellava un'intera sessione di lavoro.
 */
export default function usePersistentReducer(reducer, storageKey, initialiser) {
    const [state, dispatch] = useReducer(reducer, undefined, () => {
        const initial = initialiser();
        try {
            const raw = window.localStorage.getItem(storageKey);
            if (!raw) return initial;
            const saved = JSON.parse(raw);
            return saved?.version === initial.version ? saved : initial;
        } catch {
            return initial;
        }
    });

    const first = useRef(true);
    useEffect(() => {
        if (first.current) {
            first.current = false;
        }
        try {
            window.localStorage.setItem(storageKey, JSON.stringify(state));
        } catch {
            /* storage non disponibile: si continua solo in memoria */
        }
    }, [state, storageKey]);

    return [state, dispatch];
}

/**
 * useState con persistenza su localStorage.
 *
 * Il reducer qui sopra salva l'asta; questo salva *dove eravamo dentro l'asta*
 * — la schermata aperta, il giocatore sul tabellone, il partecipante a cui si
 * sta per assegnare. Chiudere la scheda per sbaglio non deve costare nemmeno
 * la selezione in corso, non solo le rose.
 *
 * Il valore deve essere serializzabile: si tengono id e stringhe, non oggetti
 * del listone (quelli si rileggono dal catalogo, che e' l'unica copia buona).
 */
export function usePersistentState(storageKey, fallback) {
    const [value, setValue] = useState(() => {
        try {
            const raw = window.localStorage.getItem(storageKey);
            return raw === null ? fallback : JSON.parse(raw);
        } catch {
            return fallback;
        }
    });

    useEffect(() => {
        try {
            window.localStorage.setItem(storageKey, JSON.stringify(value));
        } catch {
            /* storage non disponibile: si continua solo in memoria */
        }
    }, [value, storageKey]);

    // Riferimento stabile: finisce fra le dipendenze dei callback che scelgono
    // un giocatore, e un setter nuovo a ogni render li invaliderebbe tutti.
    const set = useCallback((next) => setValue(next), []);

    return [value, set];
}
